import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserRole, selectIsAuthenticated, selectUser } from '../../../store/slices/authSlice';
import { ROLES } from '../../../api/constants';
import ErrorLayout from '../components/ErrorLayout';
import { useThemeContext } from '../../../components/ThemeProvider';
import { ShieldX, ArrowLeft, Home, LogOut } from 'lucide-react';

const Error403 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useSelector(selectUserRole);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const { primaryColor } = useThemeContext();

  // Get the attempted path from location state or current path
  const attemptedPath = location.state?.from?.pathname || location.pathname;

  // Determine default route based on user role
  const getDefaultRoute = () => {
    if (!isAuthenticated) return '/login';
    
    if (userRole === ROLES.ADMIN) {
      return '/admin/dashboard';
    }
    return '/user/dashboard';
  };

  const handleGoBack = () => {
    // Try to go back in history
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(getDefaultRoute(), { replace: true });
    }
  };

  const handleGoHome = () => {
    navigate(getDefaultRoute(), { replace: true });
  };

  const handleLogout = () => {
    navigate('/login', { replace: true });
  };

  // Get user-friendly role name
  const getRoleName = () => {
    switch (userRole) {
      case ROLES.ADMIN: return 'Administrator';
      case ROLES.TENANT_ADMIN: return 'Tenant Admin';
      case ROLES.USER: return 'User';
      default: return 'Bilinmeyen';
    }
  };

  return (
    <ErrorLayout
      code={403}
      title="Erişim Engellendi"
      message={`Bu sayfaya erişim yetkiniz bulunmuyor. "${attemptedPath}" için gerekli izinlere sahip değilsiniz.`}
      action={
        <div className="space-y-4">
          {/* User Info */}
          {isAuthenticated && user && (
            <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-center gap-2">
                <ShieldX className="w-4 h-4" />
                <span>
                  <strong>{user.email}</strong> olarak giriş yaptınız ({getRoleName()})
                </span>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button 
              onClick={handleGoBack}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-gray-700 dark:text-gray-200 font-medium border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Geri Dön
            </button>
            <button 
              onClick={handleGoHome}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor || '#1E40AF' }}
            >
              <Home className="w-4 h-4" />
              Dashboard'a Git
            </button>
          </div>
          
          {/* Alternative: Login with different account */}
          {isAuthenticated && (
            <div className="pt-2">
              <button 
                onClick={handleLogout}
                className="inline-flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <LogOut className="w-3 h-3" />
                Farklı hesapla giriş yap
              </button>
            </div>
          )}
        </div>
      }
    />
  );
};

export default Error403;


